import { supabase } from "./supabase";
import type { FaixaProgressiva, ParametrosFolha } from "./folhaDp";

/**
 * O lado de IO do HUB DP: o que vem do banco.
 *
 * A divisão é a de sempre na casa — `folhaDp`, `feriasDecimoDp`, `encargosDp`,
 * `esocialDp` e `admissaoDp` são puros e têm prova sem navegador; este tem rede,
 * e por isso não tem regra nenhuma dentro.
 *
 * A ferramenta é EFÊMERA: nada do que o aluno digita é gravado. Não há tabela de
 * estado, e portanto não há RPC de escrita aqui — só a leitura dos parâmetros. É
 * o mesmo corte do Simulador MEI (000098).
 */

/**
 * A conversão numeric → number, na FRONTEIRA com o servidor.
 *
 * Hoje `dp_parametros_folha()` devolve `jsonb`, e o jsonb serializa `numeric`
 * como número JSON — então isto parece supérfluo. Não é: se um dia a função
 * passar a devolver `TABLE(...)`, o PostgREST entrega `numeric` como STRING para
 * não perder precisão, e `"1621.00" + 1` em JavaScript é `"1621.001"`. O holerite
 * sairia com um valor plausível e errado, sem erro nenhum.
 *
 * É a cicatriz que o DAS do MEI pagou. A conversão fica num lugar só, aqui.
 */
const n = (v: unknown): number => Number(v ?? 0);

/** Uma faixa como o banco a devolve. */
interface FaixaCrua {
  limite: unknown;
  aliquota: unknown;
  parcela_deduzir: unknown;
}

/**
 * `limite` nulo é "sem teto" e tem de CONTINUAR nulo.
 *
 * Passá-lo por `Number(null)` daria `0`, e uma faixa com teto zero não cobre
 * nada — o IRRF de quem ganha acima da penúltima faixa sairia zero, em silêncio.
 * É por isso que este campo não usa o `n()` dos outros.
 */
function converterFaixa(f: FaixaCrua): FaixaProgressiva {
  return {
    limite: f.limite === null || f.limite === undefined ? null : Number(f.limite),
    aliquota: n(f.aliquota),
    parcelaDeduzir: n(f.parcela_deduzir),
  };
}

/**
 * Os parâmetros da folha, do banco (000108).
 *
 * Devolve `null` em qualquer falha — inclusive na recusa por falta do card. A
 * tela mostra a mesma mensagem nos dois casos: quem chega sem o HUB DP liberado
 * veio pela URL digitada à mão, e não há nada de útil a dizer sobre a diferença
 * entre "sem permissão" e "fora do ar".
 *
 * E devolve `null` também quando as faixas vêm vazias. Sem essa guarda, a
 * calculadora abriria e estouraria "a tabela do INSS não tem faixa que cubra a
 * base" no primeiro cálculo — erro técnico numa tela de aluno, em vez de um aviso
 * de que a ferramenta está sendo configurada.
 */
export async function lerParametrosFolha(): Promise<ParametrosFolha | null> {
  const { data, error } = await supabase.rpc("dp_parametros_folha");
  if (error || !data) return null;

  const p = data as Record<string, any>;
  const faixasCruas = (p.faixas ?? {}) as Record<string, FaixaCrua[]>;
  const inss = Array.isArray(faixasCruas.inss) ? faixasCruas.inss.map(converterFaixa) : [];
  const irrf = Array.isArray(faixasCruas.irrf) ? faixasCruas.irrf.map(converterFaixa) : [];
  if (inss.length === 0 || irrf.length === 0) return null;

  const r = (p.reducao_ir ?? {}) as Record<string, unknown>;

  return {
    salarioMinimo: n(p.salario_minimo),
    deducaoDependente: n(p.deducao_dependente),
    descontoSimplificado: n(p.desconto_simplificado),
    reducaoIr: {
      limiteIntegral: n(r.limite_integral),
      valorIntegral: n(r.valor_integral),
      limiteFinal: n(r.limite_final),
      base: n(r.base),
      coeficiente: n(r.coeficiente),
    },
    inssTeto: n(p.inss_teto),
    inssDescontoMaximo: n(p.inss_desconto_maximo),
    faixas: { inss, irrf },
    // Texto: não passa pelo `n()`, e o `?? ""` evita "null" aparecendo na tela.
    referenciaInss: String(p.referencia_inss ?? ""),
    referenciaIrrf: String(p.referencia_irrf ?? ""),
  };
}

/**
 * As duas portas de escrita, para a tela de administração (só admin).
 *
 * Devolvem a mensagem de erro do banco, e não um booleano: as recusas aqui são
 * informativas de propósito ("a alíquota da faixa 2 é menor que a anterior") e
 * quem está do outro lado é a equipe que mantém a tabela — engolir o texto a
 * faria adivinhar o que recusou.
 */
export async function salvarParametrosFolha(campos: {
  salarioMinimo?: number;
  deducaoDependente?: number;
  descontoSimplificado?: number;
  reducaoLimiteIntegral?: number;
  reducaoValorIntegral?: number;
  reducaoLimiteFinal?: number;
  reducaoBase?: number;
  reducaoCoeficiente?: number;
  referenciaInss?: string;
  referenciaIrrf?: string;
}): Promise<string | null> {
  const { error } = await supabase.rpc("dp_atualizar_parametros", {
    p_salario_minimo: campos.salarioMinimo ?? null,
    p_deducao_dependente: campos.deducaoDependente ?? null,
    p_desconto_simplificado: campos.descontoSimplificado ?? null,
    p_reducao_limite_integral: campos.reducaoLimiteIntegral ?? null,
    p_reducao_valor_integral: campos.reducaoValorIntegral ?? null,
    p_reducao_limite_final: campos.reducaoLimiteFinal ?? null,
    p_reducao_base: campos.reducaoBase ?? null,
    p_reducao_coeficiente: campos.reducaoCoeficiente ?? null,
    p_referencia_inss: campos.referenciaInss ?? null,
    p_referencia_irrf: campos.referenciaIrrf ?? null,
  });
  return error ? error.message : null;
}

/**
 * Substitui a tabela progressiva de um tributo INTEIRA.
 *
 * Não existe salvar uma faixa isolada, e o motivo está na 000108: o estado
 * intermediário (limite novo, parcela velha) daria número errado sem erro na
 * tela, e todo aluno que abrisse a calculadora naquele intervalo receberia um
 * holerite incorreto.
 */
export async function salvarFaixas(
  tributo: "inss" | "irrf",
  faixas: FaixaProgressiva[],
): Promise<string | null> {
  const { error } = await supabase.rpc("dp_definir_faixas", {
    p_tributo: tributo,
    p_faixas: faixas.map((f) => ({
      limite: f.limite,
      aliquota: f.aliquota,
      parcela_deduzir: f.parcelaDeduzir,
    })),
  });
  return error ? error.message : null;
}
